import React, { useState, useEffect, useContext } from 'react';

import Grid from '@mui/material/Grid';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import FlaggedDevicesSubList from '../components/FlaggedDevicesSubList';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';

import { BackflowJobItem } from '../../model';
import { NavigationContext } from '../../../adminModules/navigation/contexts/NavigationContext';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

import { minimumLoading } from '../../../utils/minimumLoading';

import axios from '../../../sharedModules/utils/axios.utils';

const FlaggedDevicesListPage: React.FunctionComponent = () => {
  const { fetchBadgeCounts } = useContext(NavigationContext);

  const { user } = useContext(AuthenticationContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [newDevices, setNewDevices] = useState<BackflowJobItem[]>();
  const [outOfServiceDevices, setOutOfServiceDevices] = useState<BackflowJobItem[]>();

  const fetchFlaggedDevices = async () => {
    try {
      const response: any = await axios.get('/backflows/backflow_job_items', {
        params: {
          'ransack[processed_eq]': 'false',
          'ransack[backflow_job_group_status_in]': ['approved', 'complete'],
        },
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => searchParams.append(`${key}[]`, v));
            } else {
              searchParams.append(key, value as string);
            }
          });
          return searchParams.toString();
        },
      });

      const allFlaggedDevices = response.data.result;

      const out = allFlaggedDevices.filter((d) => d.status === 'out_of_service');
      setOutOfServiceDevices(out);

      // All devices that were not included in the first group.
      // Theoretically, this should only include newly-created devices.
      // But this is written to catch discrepancies
      const newOnes = allFlaggedDevices.filter((d) => !out.some((o) => o.id === d.id));
      setNewDevices(newOnes);
      fetchBadgeCounts('backflows');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong fetching the Devices');
    }
  };

  useEffect(() => {
    if (user) {
      minimumLoading(fetchFlaggedDevices, setLoading);
    }
  }, [user]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <LoadingWrapper isLoading={loading}>
          <Grid container>
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <PageHeader mainTitle="Backflow Testing" subTitle="Flagged Devices" />
            </Grid>
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage} />
            </Grid>
            <Grid item xs={12}>
              <FlaggedDevicesSubList
                jobItemsSubList={outOfServiceDevices}
                afterComplete={fetchFlaggedDevices}
                title="Out of Service"
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: 20 }}>
              <FlaggedDevicesSubList
                jobItemsSubList={newDevices}
                afterComplete={fetchFlaggedDevices}
                title="Newly-Added"
              />
            </Grid>
          </Grid>
        </LoadingWrapper>
      </Grid>
    </Grid>
  );
};

export default FlaggedDevicesListPage;
